import { useAtomValue } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { type MeasureBundleObject, useShouldPromptForCSSRS } from '@grow-therapy-team/seedling-components';

import { useTrackEvent } from '../../segment/segment';
import { EventTypeEnum } from '../../segment/types';
import { toast } from '../../toasts';
import { roomAtom } from '../../video-sdk';
import { useOpenClientInformationCallback } from '../client-information/useClientInformationDrawerCallbacks';
import { ProviderDrawerState, providerShortIdAtom } from '../state';
import { CSSRSPromptToast } from './CSSRSPromptToast';
import { getCSSRSPromptToastCopy } from './utils';

const TOAST_KEY = 'cssrs-prompt';

type PromptPayload = {
  visitorName: string;
  visitorUuid: string;
  patientShortId: string;
  appointmentShortId?: string;
  patientChart: {
    preferredShortName?: string | null;
    measureBundles?: MeasureBundleObject[];
  };
};

export function useCSSRSPromptToastCallback() {
  const room = useAtomValue(roomAtom);
  const providerShortId = useAtomValue(providerShortIdAtom);
  const { userViewed } = useTrackEvent();
  const openClientInformation = useOpenClientInformationCallback();
  const [promptPayload, setPromptPayload] = useState<PromptPayload | null>(null);

  const { loading, result } = useShouldPromptForCSSRS({
    patientShortId: promptPayload?.patientShortId ?? '',
    measureBundles: promptPayload?.patientChart?.measureBundles ?? [],
    appointmentShortId: promptPayload?.appointmentShortId,
  });

  const patientDisplayName = promptPayload
    ? promptPayload.patientChart.preferredShortName || promptPayload.visitorName
    : '';

  const toastCopy = useMemo(
    () =>
      !promptPayload || loading
        ? undefined
        : getCSSRSPromptToastCopy(result, patientDisplayName, true, true, promptPayload.patientChart.measureBundles),
    [promptPayload, loading, result, patientDisplayName],
  );

  const triggerSource = result.triggerSource;
  const cssrsType = result.cssrsType;

  useEffect(
    function clearToastOnRoomDisconnect() {
      if (!room) {
        toast.close(TOAST_KEY);
      }
    },
    [room],
  );

  useEffect(
    function enqueueAfterPromptResolved() {
      if (!promptPayload || loading) {
        return;
      }
      if (!toastCopy) {
        setPromptPayload(null);
        return;
      }

      const payload = promptPayload;
      setPromptPayload(null);

      const toastKey = toast.enqueue(
        ({ closeToast, ...ariaProps }) => (
          <CSSRSPromptToast
            {...ariaProps}
            heading={toastCopy.heading}
            body={toastCopy.body}
            variant={toastCopy.variant}
            callToAction={toastCopy.callToAction}
            onAdministerCSSRS={() => {
              openClientInformation(
                { visitorUuid: payload.visitorUuid, patientShortId: payload.patientShortId },
                ProviderDrawerState.CSSRS_FORM,
              );
              closeToast();
            }}
          />
        ),
        { key: TOAST_KEY, timeout: Infinity },
      );

      if (toastKey) {
        userViewed(EventTypeEnum.CSSRS_PROMPT_TOAST_SHOWN, {
          patientShortId: payload.patientShortId,
          providerShortId: providerShortId ?? '',
          ...(payload.appointmentShortId ? { appointmentShortId: payload.appointmentShortId } : {}),
          ...(triggerSource ? { triggerSource } : {}),
          ...(cssrsType ? { cssrsType } : {}),
        });
      }
    },
    [promptPayload, loading, toastCopy, openClientInformation, userViewed, providerShortId, triggerSource, cssrsType],
  );

  return useCallback(
    (
      {
        visitorName,
        visitorUuid,
        patientShortId,
        appointmentShortId,
      }: {
        visitorName: string;
        visitorUuid: string;
        patientShortId: string;
        appointmentShortId?: string;
      },
      patientChart?: {
        preferredShortName?: string | null;
        measureBundles?: MeasureBundleObject[];
      } | null,
    ) => {
      if (!patientChart) {
        return;
      }

      if (!patientChart.measureBundles?.length) {
        return;
      }

      setPromptPayload({ visitorName, visitorUuid, patientShortId, appointmentShortId, patientChart });
    },
    [],
  );
}
